import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import { TableActions } from '@/Components/ui/TableActions';
import responsavelService, { type Responsavel } from '@/services/responsavel';

export default function Responsaveis() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Responsavel[]>([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await responsavelService.listar();
      setItems(data);
    } catch (error) {
      console.error('Erro ao carregar responsáveis', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const filtrados = useMemo(() => {
    let resultado = items;
    if (busca) {
      const termo = busca.toLowerCase();
      resultado = resultado.filter(
        (r) =>
          r.nome?.toLowerCase().includes(termo) ||
          r.cpf?.includes(termo) ||
          r.cargo?.toLowerCase().includes(termo)
      );
    }
    if (filtroAtivo !== '') {
      resultado = resultado.filter((r) => r.ativo === (filtroAtivo === 'true'));
    }
    return resultado;
  }, [items, busca, filtroAtivo]);

  const deletar = async (item: Responsavel) => {
    try {
      await responsavelService.excluir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir responsável', error);
      throw error;
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<UserRound size={21} strokeWidth={2} />}
        title="Responsáveis"
        description="Cadastro dos responsáveis técnicos envolvidos na industrialização."
        actions={
          <Button type="button" onClick={() => navigate('/producao/responsaveis/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Novo Responsável
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar responsável..."
                />
              </div>
              <Select filter value={filtroAtivo} onChange={(e) => setFiltroAtivo(e.target.value)}>
                <option value="">Todos</option>
                <option value="true">Ativos</option>
                <option value="false">Inativos</option>
              </Select>
            </div>
          }
        />

        {!loading && filtrados.length === 0 ? (
          <EmptyState message="Nenhum responsável encontrado." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CPF</th>
                  <th>Cargo</th>
                  <th>E-mail</th>
                  <th>Telefone</th>
                  <th>Status</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  filtrados.map((item) => (
                    <tr key={item.id}>
                      <td>{item.nome}</td>
                      <td>{item.cpf}</td>
                      <td>{item.cargo}</td>
                      <td>{item.email}</td>
                      <td>{item.telefone}</td>
                      <td>
                        <StatusBadge label={item.ativo ? 'Ativo' : 'Inativo'} tone={item.ativo ? 'success' : 'danger'} />
                      </td>
                      <td className="nx-col-acoes">
                        <TableActions
                          onEdit={() => navigate(`/producao/responsaveis/${item.id}/editar`)}
                          onDelete={() => deletar(item)}
                          deleteTitle="Excluir responsável"
                          deleteDescription={`Tem certeza de que deseja excluir o responsável "${item.nome}"? Esta ação não poderá ser desfeita.`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
